// reCAPTCHA v3 helper. Genera un token efímero por acción que el backend verifica.
// Si no hay site key configurada (ej: dev local), devuelve null y los endpoints
// pueden decidir qué hacer (aceptar o rechazar según política).

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

const SITE_KEY = (import.meta as any).env?.VITE_RECAPTCHA_SITE_KEY as string | undefined;

export const RECAPTCHA_ENABLED = !!SITE_KEY;

let readyPromise: Promise<void> | null = null;

function waitForGrecaptcha(): Promise<void> {
  if (readyPromise) return readyPromise;
  readyPromise = new Promise((resolve) => {
    const tick = () => {
      if (typeof window !== 'undefined' && window.grecaptcha?.ready) {
        window.grecaptcha.ready(() => resolve());
      } else {
        setTimeout(tick, 100);
      }
    };
    tick();
  });
  return readyPromise;
}

/**
 * Devuelve un token reCAPTCHA v3 para la acción indicada, o null si no hay
 * site key configurada (entornos sin captcha).
 *
 * Action es un identificador semántico de la operación (p.ej. "register",
 * "lead_capture", "generate_image") que ayuda a Google a calcular el score.
 */
export async function getRecaptchaToken(action: string): Promise<string | null> {
  if (!SITE_KEY || typeof window === 'undefined') return null;
  try {
    await waitForGrecaptcha();
    if (!window.grecaptcha) return null;
    return await window.grecaptcha.execute(SITE_KEY, { action });
  } catch (err) {
    console.warn('[recaptcha] token generation failed:', err);
    return null;
  }
}
